import { useTranslation } from "next-i18next";
import qs from "querystring";
import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { DEFAULT_OFFSET_VALUE, DEFAULT_PER_PAGE_VALUE } from "@/shared/constants";

import { UI_CONTEXTS } from "@/analytics/constants";

import { useCurrentLocale } from "@/context/LanguageContext";

import Loader from "@/components/loaders/loader";
import { ProductTiles } from "@/components/product-tiles/product-tiles";

import { useCollectionProducts } from "@/hooks/useCollection";

import { CollectionV2Props } from "@/interfaces/collection-v2-interface";
import { ProductProps } from "@/interfaces/products-interface";

import styles from "./productCollection.module.scss";

interface Props {
    currentCollection: CollectionV2Props;
}

const ProductCollection: React.FC<Props> = ({ currentCollection }) => {
    const { name } = currentCollection || {};

    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const [offset, setOffset] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [queryString, setQueryString] = useState<string>(
        qs.stringify({
            offset: offset,
            per_page: DEFAULT_PER_PAGE_VALUE,
        })
    );
    const [productsList, setProductsList] = useState<Array<ProductProps>>([]);
    const products = useCollectionProducts(currentCollection.slug, {
        queryString,
        initialState: null,
    });

    const loadMoreProducts = () => {
        setOffset(offset + DEFAULT_OFFSET_VALUE);
    };

    useEffect(() => {
        setProductsList([]);
        setOffset(0);
    }, [locale]);

    useEffect(() => {
        if (!Array.isArray(products)) {
            setHasMore(true);
            return;
        }

        setProductsList((prev) => [...prev, ...products]);
        setHasMore(!!products.length);
    }, [products]);

    useEffect(() => {
        setQueryString(
            qs.stringify({
                offset: offset,
                per_page: DEFAULT_PER_PAGE_VALUE,
            })
        );
    }, [offset, locale]);
    return (
        <React.Fragment>
            <div className={styles.collectionDescriptionContainer}>
                <h3 className={styles.collectionDescriptionHeader}>{name}</h3>
                <div className={styles.collectionDescriptionSubtitle}>{`${
                    currentCollection.totalProductCount
                } ${t("collections.products")}`}</div>
            </div>
            {!hasMore && !productsList.length ? (
                <div className={styles.emptyResultsContainer}>
                    <div className={styles.emptyResultsText}>
                        <p>{t("store.filters.notFound.noResults")}</p>
                    </div>
                </div>
            ) : (
                <div className={styles.productsCollectionContainer}>
                    <InfiniteScroll
                        dataLength={productsList.length}
                        hasMore={hasMore}
                        loader={<Loader />}
                        next={loadMoreProducts}
                        scrollThreshold={0.8}
                    >
                        <ProductTiles
                            category={currentCollection}
                            products={productsList}
                            uiContext={UI_CONTEXTS.COLLECTION_TILE}
                        />
                    </InfiniteScroll>
                </div>
            )}
        </React.Fragment>
    );
};

export default ProductCollection;
